import React, { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { supabase, TABLES } from '../../config/supabase'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Card from '../ui/Card'
import Button from '../ui/Button'
import Switch from '../ui/Switch'
import toast from 'react-hot-toast'

const { FiBell, FiMail, FiSmartphone, FiClock, FiCheck, FiTrendingUp } = FiIcons

const NotificationSettings = () => {
  const { family, setFamily } = useAuth()
  const [loading, setLoading] = useState(false)
  const [settings, setSettings] = useState(
    family?.settings?.notifications || {
      task_reminders: true,
      completion_alerts: true,
      weekly_summary: true,
      reward_available: true,
      level_up: true,
      email_notifications: true,
      push_notifications: false,
      reminder_time: '18:00',
      summary_day: 'sunday'
    }
  )

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const saveSettings = async () => {
    setLoading(true)
    try {
      const updatedSettings = {
        ...family.settings,
        notifications: settings
      }

      const { data, error } = await supabase
        .from(TABLES.FAMILIES)
        .update({ settings: updatedSettings })
        .eq('id', family.id)
        .select()
        .single()

      if (error) {
        console.error('Settings update error:', error)
        toast.error('Failed to save notification settings')
      } else {
        setFamily(data)
        toast.success('Notification settings saved! 🔔')
      }
    } catch (error) {
      console.error('Unexpected error:', error)
      toast.error('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const notificationTypes = [
    {
      key: 'task_reminders',
      title: 'Task Reminders',
      description: 'Remind children about pending tasks',
      icon: FiClock,
      color: 'text-blue-500'
    },
    {
      key: 'completion_alerts',
      title: 'Completion Alerts',
      description: 'Get notified when tasks are completed',
      icon: FiCheck,
      color: 'text-green-500'
    },
    {
      key: 'weekly_summary',
      title: 'Weekly Summary',
      description: 'Receive a summary of the week\'s progress',
      icon: FiTrendingUp,
      color: 'text-purple-500'
    },
    {
      key: 'reward_available',
      title: 'Reward Notifications',
      description: 'Alert when children can redeem rewards',
      icon: FiTrendingUp,
      color: 'text-yellow-500'
    },
    {
      key: 'level_up',
      title: 'Level Up Alerts',
      description: 'Celebrate when children reach new levels',
      icon: FiTrendingUp,
      color: 'text-orange-500'
    }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <div className="flex items-center gap-3 mb-4">
          <SafeIcon icon={FiBell} className="text-primary-500 text-xl" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Notification Settings</h2>
            <p className="text-gray-600 mt-1">
              Control how and when you receive notifications about family activities
            </p>
          </div>
        </div>
      </Card>

      {/* Notification Types */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Notification Types</h3>
        <div className="space-y-4">
          {notificationTypes.map((type) => (
            <div
              key={type.key}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <SafeIcon icon={type.icon} className={`${type.color} text-lg`} />
                <div>
                  <div className="font-medium text-gray-900">{type.title}</div>
                  <div className="text-sm text-gray-500">{type.description}</div>
                </div>
              </div>
              <Switch
                checked={settings[type.key]}
                onChange={(checked) => handleSettingChange(type.key, checked)}
              />
            </div>
          ))}
        </div>
      </Card>

      {/* Delivery Methods */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Delivery Methods</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiMail} className="text-blue-500 text-lg" />
              <div>
                <div className="font-medium text-gray-900">Email Notifications</div>
                <div className="text-sm text-gray-500">Receive notifications via email</div>
              </div>
            </div>
            <Switch
              checked={settings.email_notifications}
              onChange={(checked) => handleSettingChange('email_notifications', checked)}
            />
          </div>

          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiSmartphone} className="text-green-500 text-lg" />
              <div>
                <div className="font-medium text-gray-900">Push Notifications</div>
                <div className="text-sm text-gray-500">Receive browser push notifications</div>
              </div>
            </div>
            <Switch
              checked={settings.push_notifications}
              onChange={(checked) => handleSettingChange('push_notifications', checked)}
            />
          </div>
        </div>
      </Card>

      {/* Timing Settings */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Timing & Schedule</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Daily Reminder Time
            </label>
            <input
              type="time"
              value={settings.reminder_time}
              onChange={(e) => handleSettingChange('reminder_time', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              When to send daily task reminders
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Weekly Summary Day
            </label>
            <select
              value={settings.summary_day}
              onChange={(e) => handleSettingChange('summary_day', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="sunday">Sunday</option>
              <option value="monday">Monday</option>
              <option value="tuesday">Tuesday</option>
              <option value="wednesday">Wednesday</option>
              <option value="thursday">Thursday</option>
              <option value="friday">Friday</option>
              <option value="saturday">Saturday</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Day to receive weekly progress summary
            </p>
          </div>
        </div>
      </Card>

      {/* Test Notifications */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Test Notifications</h3>
        <p className="text-gray-600 mb-4">
          Send yourself a test notification to make sure everything is working correctly.
        </p>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => toast.success('Test email notification sent! 📧')}
          >
            <SafeIcon icon={FiMail} className="text-sm" />
            Test Email
          </Button>
          <Button
            variant="outline"
            onClick={() => toast.success('Test push notification sent! 📱')}
          >
            <SafeIcon icon={FiSmartphone} className="text-sm" />
            Test Push
          </Button>
        </div>
      </Card>

      {/* Privacy Notice */}
      <Card className="bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <SafeIcon icon={FiBell} className="text-blue-600 text-lg mt-1" />
          <div>
            <h4 className="font-medium text-blue-800 mb-1">Privacy Notice</h4>
            <p className="text-sm text-blue-700">
              We respect your privacy. Notifications contain only essential information about your family's 
              activities. You can unsubscribe or modify these settings at any time. We never share your 
              notification preferences with third parties.
            </p>
          </div>
        </div>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={saveSettings}
          loading={loading}
        >
          <SafeIcon icon={FiBell} className="text-sm" />
          Save Notification Settings
        </Button>
      </div>
    </div>
  )
}

export default NotificationSettings